import { ApiReportReportTypeChoices, ReportSectionsType } from "@api/graphql/types-and-hooks";
import {
  useIsReportCompleted,
  useIsReportHandlebars,
  useIsReviewPage,
} from "@components/ReportField/capabilities/flags";
import { APPENDIX_PAGE_LOCATION, RECOMMENDATIONS_PAGE_LOCATION } from "@components/ReportField/consts";
import { useAdminData } from "@providers/AdminDataProvider";
import { useReport } from "@providers/ReportProvider";

// Whether "Move Up" / "Move Down" show. Legacy FieldBlockCardWrapper only offered
// reorder for handlebars exports (assessment/clinical), for editable evaluation
// sections, and for clinical recommendation/appendix sections.
export const useCanReorderField = (section: ReportSectionsType): boolean => {
  const { report, viewMode } = useReport();
  const { isCatalightUser } = useAdminData();
  const isReviewPage = useIsReviewPage();
  const isHandlebars = useIsReportHandlebars();
  const isCompleted = useIsReportCompleted();

  const reportType = report.reportType;
  const onAppendix = section.pageLocation === APPENDIX_PAGE_LOCATION;
  const onRecommendations = section.pageLocation === RECOMMENDATIONS_PAGE_LOCATION;

  if (reportType === ApiReportReportTypeChoices.InitialAssessment) {
    return isHandlebars && !isReviewPage;
  }
  if (reportType === ApiReportReportTypeChoices.DiagnosisEvaluation && viewMode === "step") {
    const isBaseTemplate = report.reportTemplate?.isBaseTemplate ?? false;
    return isBaseTemplate || onAppendix || (onRecommendations && !isCatalightUser);
  }

  // Clinical: a completed report is locked, so nothing can be reordered.
  if (isCompleted) return false;
  if (isHandlebars) return true;
  // Only recommendation/appendix sections reorder — never a plain regular section.
  if (!onRecommendations && !onAppendix) return false;
  const appendixSupported = reportType === ApiReportReportTypeChoices.DiagnosisEvaluation && viewMode === "single";
  if (appendixSupported && onAppendix) return true;
  if (onRecommendations) return !isCatalightUser && !isReviewPage;
  return false;
};
